// src/interfaces/manager/ManagerNavigation.js
import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useQuotes } from '../../context/QuoteContext';

const ManagerNavigation = ({ activeSection, setActiveSection, showSettingsMode, setShowSettingsMode }) => {
  const { currentUser, logout, isAdmin } = useAuth(); // Add isAdmin
  const { stopEditingDraft } = useQuotes();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false); // State for mobile menu
  
  const handleLogout = async () => {
    try {
      await logout();
      // Optional: Redirect or perform other actions after logout if needed
      // e.g., window.location.href = '/login';
    } catch (error) {
      console.error("Logout failed:", error);
      // Handle logout error if necessary
    }
  };
  
  return (
    <nav className="bg-gray-800 shadow">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between h-16">
          <div className="flex">
            <div className="flex-shrink-0 flex items-center">
              <span className="text-xl font-bold text-white">Manager Portal</span>
            </div>
            <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
              {!showSettingsMode ? (
                <>
                  {/* Operations Mode - Show individual operation buttons */}
                  <button
                    onClick={() => { stopEditingDraft(); setActiveSection('quotes'); }}
                    className={`${
                      activeSection === 'quotes'
                        ? 'border-blue-500 text-white'
                        : 'border-transparent text-gray-300 hover:border-gray-300 hover:text-white'
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-full`}
                  >
                    Quote Management
                  </button>
                  
                  <button
                    onClick={() => { stopEditingDraft(); setActiveSection('pricingCalculator'); }}
                    className={`${
                      activeSection === 'pricingCalculator'
                        ? 'border-blue-500 text-white'
                        : 'border-transparent text-gray-300 hover:border-gray-300 hover:text-white'
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-full`}
                  >
                    Pricing Calculator
                  </button>

                  <button
                    onClick={() => { stopEditingDraft(); setActiveSection('customPricing'); }}
                    className={`${
                      activeSection === 'customPricing'
                        ? 'border-blue-500 text-white'
                        : 'border-transparent text-gray-300 hover:border-gray-300 hover:text-white'
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-full`}
                  >
                    Custom Pricing
                  </button>

                  <button
                    onClick={() => { stopEditingDraft(); setActiveSection('partnerInsights'); }}
                    className={`${
                      activeSection === 'partnerInsights'
                        ? 'border-blue-500 text-white'
                        : 'border-transparent text-gray-300 hover:border-gray-300 hover:text-white'
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-full`}
                  >
                    Partner Insights
                  </button>
                  
                  {isAdmin() && (
                    <button
                      onClick={() => setShowSettingsMode(true)}
                      className="border-transparent text-gray-300 hover:border-gray-300 hover:text-white inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-full"
                    >
                      Settings
                    </button>
                  )}
                </>
              ) : (
                <>
                  {/* Settings Mode - Show collapsed operations button and individual admin buttons */}
                  <button
                    onClick={() => setShowSettingsMode(false)}
                    className={`${
                      ['quotes', 'pricingCalculator', 'partnerInsights'].includes(activeSection)
                        ? 'border-blue-500 text-white'
                        : 'border-transparent text-gray-300 hover:border-gray-300 hover:text-white'
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-full`}
                  >
                    Operations
                  </button>
                  
                  <button
                    onClick={() => setActiveSection('discounts')}
                    className={`${
                      activeSection === 'discounts'
                        ? 'border-blue-500 text-white'
                        : 'border-transparent text-gray-300 hover:border-gray-300 hover:text-white'
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-full`}
                  >
                    Volume Discounts
                  </button>
                  
                  <button
                    onClick={() => setActiveSection('pricingCurves')}
                    className={`${
                      activeSection === 'pricingCurves'
                        ? 'border-blue-500 text-white'
                        : 'border-transparent text-gray-300 hover:border-gray-300 hover:text-white'
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-full`}
                  >
                    Pricing Curves
                  </button>
                  
                  <button
                    onClick={() => setActiveSection('database')}
                    className={`${
                      activeSection === 'database'
                        ? 'border-blue-500 text-white'
                        : 'border-transparent text-gray-300 hover:border-gray-300 hover:text-white'
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-full`}
                  >
                    Database
                  </button>
                  
                  <button
                    onClick={() => setActiveSection('userManagement')}
                    className={`${
                      activeSection === 'userManagement'
                        ? 'border-blue-500 text-white'
                        : 'border-transparent text-gray-300 hover:border-gray-300 hover:text-white'
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-full`}
                  >
                    User Management
                  </button>

                  <button
                    onClick={() => setActiveSection('customPricing')}
                    className={`${
                      activeSection === 'customPricing'
                        ? 'border-blue-500 text-white'
                        : 'border-transparent text-gray-300 hover:border-gray-300 hover:text-white'
                    } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium h-full`}
                  >
                    Custom Pricing
                  </button>
                </>
              )}
            </div>
          </div>
          
          {/* NEW: Desktop user info and logout */}
          <div className="hidden sm:flex sm:items-center sm:space-x-4">
            {currentUser && (
              <>
                <div className="text-sm text-gray-300">
                  Welcome, {currentUser.username || 'Account Manager'}
                </div>
                <button
                  onClick={handleLogout}
                  className="text-gray-300 hover:text-white px-3 py-2 rounded-md text-sm font-medium border border-gray-600 hover:bg-gray-700 hover:border-gray-500"
                >
                  Logout
                </button>
              </>
            )}
          </div>
          
          {/* Mobile menu button (Hamburger) */}
          <div className="-mr-2 flex items-center sm:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              type="button"
              className="bg-gray-800 inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              aria-controls="mobile-menu"
              aria-expanded={isMobileMenuOpen}
            >
              <span className="sr-only">Open main menu</span>
              {/* Icon when menu is closed. */}
              {!isMobileMenuOpen ? (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              ) : (
                // Icon when menu is open.
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu, show/hide based on menu state. */}
        {isMobileMenuOpen && (
          <div className="sm:hidden" id="mobile-menu">
            <div className="pt-2 pb-3 space-y-1">
              {!showSettingsMode ? (
                <>
                  {/* Operations Mode - Show individual operation buttons */}
                  <button
                    onClick={() => { stopEditingDraft(); setActiveSection('quotes'); setIsMobileMenuOpen(false); }}
                    className={`${
                      activeSection === 'quotes'
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    } block px-3 py-2 rounded-md text-base font-medium w-full text-left`}
                  >
                    Quote Management
                  </button>
                  
                  <button
                    onClick={() => { stopEditingDraft(); setActiveSection('pricingCalculator'); setIsMobileMenuOpen(false); }}
                    className={`${
                      activeSection === 'pricingCalculator'
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    } block px-3 py-2 rounded-md text-base font-medium w-full text-left`}
                  >
                    Pricing Calculator
                  </button>

                  <button
                    onClick={() => { stopEditingDraft(); setActiveSection('customPricing'); setIsMobileMenuOpen(false); }}
                    className={`${
                      activeSection === 'customPricing'
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    } block px-3 py-2 rounded-md text-base font-medium w-full text-left`}
                  >
                    Custom Pricing
                  </button>

                  <button
                    onClick={() => { stopEditingDraft(); setActiveSection('partnerInsights'); setIsMobileMenuOpen(false); }}
                    className={`${
                      activeSection === 'partnerInsights'
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    } block px-3 py-2 rounded-md text-base font-medium w-full text-left`}
                  >
                    Partner Insights
                  </button>
                  
                  {isAdmin() && (
                    <button
                      onClick={() => { setShowSettingsMode(true); setIsMobileMenuOpen(false); }}
                      className="text-gray-300 hover:bg-gray-700 hover:text-white block px-3 py-2 rounded-md text-base font-medium w-full text-left"
                    >
                      Settings
                    </button>
                  )}
                </>
              ) : (
                <>
                  {/* Settings Mode - Show collapsed operations button and individual admin buttons */}
                  <button
                    onClick={() => { setShowSettingsMode(false); setIsMobileMenuOpen(false); }}
                    className={`${
                      ['quotes', 'pricingCalculator', 'partnerInsights'].includes(activeSection)
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    } block px-3 py-2 rounded-md text-base font-medium w-full text-left`}
                  >
                    Operations
                  </button>
                  
                  <button
                    onClick={() => { setActiveSection('discounts'); setIsMobileMenuOpen(false); }}
                    className={`${
                      activeSection === 'discounts'
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    } block px-3 py-2 rounded-md text-base font-medium w-full text-left`}
                  >
                    Volume Discounts
                  </button>
                  
                  <button
                    onClick={() => { setActiveSection('pricingCurves'); setIsMobileMenuOpen(false); }}
                    className={`${
                      activeSection === 'pricingCurves'
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    } block px-3 py-2 rounded-md text-base font-medium w-full text-left`}
                  >
                    Pricing Curves
                  </button>
                  
                  <button
                    onClick={() => { setActiveSection('database'); setIsMobileMenuOpen(false); }}
                    className={`${
                      activeSection === 'database'
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    } block px-3 py-2 rounded-md text-base font-medium w-full text-left`}
                  >
                    Database
                  </button>
                  
                  <button
                    onClick={() => { setActiveSection('userManagement'); setIsMobileMenuOpen(false); }}
                    className={`${
                      activeSection === 'userManagement'
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    } block px-3 py-2 rounded-md text-base font-medium w-full text-left`}
                  >
                    User Management
                  </button>

                  <button
                    onClick={() => { setActiveSection('customPricing'); setIsMobileMenuOpen(false); }}
                    className={`${
                      activeSection === 'customPricing'
                        ? 'bg-gray-900 text-white'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                    } block px-3 py-2 rounded-md text-base font-medium w-full text-left`}
                  >
                    Custom Pricing
                  </button>
                </>
              )}
            </div>
            {currentUser && ( // Only show if user is logged in
              <div className="pt-4 pb-3 border-t border-gray-700">
                <div className="flex items-center px-5">
                  {/* Optional: Add user avatar or icon here */}
                  <div className="ml-3">
                     {/* Use currentUser.username */}
                    <div className="text-base font-medium text-white">Welcome, {currentUser.username || 'Account Manager'}</div>
                    {/* Optional: Add user email here */}
                    {/* <div className="text-sm font-medium text-gray-400">{currentUser.email}</div> */}
                  </div>
                </div>
                <div className="mt-3 px-2 space-y-1">
                   {/* Add the button with border and adjusted hover */}
                  <button
                    onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }} // Use the handler function and close menu
                    className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-400 border border-gray-600 hover:text-white hover:bg-gray-700 hover:border-gray-500" // Added border, adjusted hover
                  >
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default ManagerNavigation;
